import { Grid, Paper, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';
import { Colors } from 'src/shared/components/styles/colors';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getMemberStats } from 'src/shared/services/memberService';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberStats } from 'src/modules/dashboard/models/memberStats';
import { ContactsChart } from 'src/modules/dashboard/components/members/contactsChart';
import { LargeCounter } from 'src/modules/dashboard/components/atoms/largeCounter';
import { StatBoxSmall } from 'src/modules/dashboard/components/atoms/statBoxSmall';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { ColoredBox } from 'src/modules/dashboard/components/atoms/coloredBox';
import { ChartFailMessage } from 'src/modules/dashboard/components/atoms/chartFailMessage';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { responsiveSpacing } from '../../models/responsiveSpacing';

type MemberStatsBoxesProps = {
  associationId: number | undefined;
  districtId: number | undefined;
};

export const MemberStatsBoxes = (props: MemberStatsBoxesProps) => {
  const { associationId, districtId } = props;

  const { t } = useTranslation(['dashboard']);
  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [memberStatsData, setMemberStatsData] = useState<MemberStats>();

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const result = await getMemberStats(associationId, districtId);
        if (isMounted()) {
          setMemberStatsData(result.data);
          // ConsoleHelper.log('memberStatsData', result.data);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setHasError(true);
        }
      } finally {
        if (isMounted()) {
          setIsLoading(false);
        }
      }
    };
    fetchData();
  }, [isMounted, associationId, districtId]);

  return (
    <>
      <Grid item xs={12} md={6} lg={5}>
        <Paper
          sx={{
            p: 2,
            height: '100%',
          }}
        >
          <HeaderSubTitle label={t('dashboard:members.header')} />
          {isLoading && !memberStatsData ? (
            <Skeleton variant='text' sx={{ fontSize: (theme) => theme.typography.h3 }} />
          ) : (
            <LargeCounter count={memberStatsData?.nrOfMembers || 0} displaySuffix />
          )}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <ColoredBox
              label={t('dashboard:members.coloredBox.paid')}
              isLoading={isLoading}
              count={memberStatsData?.nrOfPaidMembers || 0}
              bgColor={Colors.ChipColors.Green}
              memberStatus={MemberStatusTabKeysEnum.Paid}
            />
            <ColoredBox
              label={t('dashboard:members.coloredBox.unpaid')}
              isLoading={isLoading}
              count={memberStatsData?.nrOfUnPaidMembers || 0}
              bgColor={Colors.ChipColors.Red}
              memberStatus={MemberStatusTabKeysEnum.NotPaid}
            />
            <ColoredBox
              label={t('dashboard:members.coloredBox.interested')}
              isLoading={isLoading}
              count={memberStatsData?.nrOfNewAppliedMembers || 0}
              bgColor={Colors.ChipColors.Yellow}
              memberStatus={MemberStatusTabKeysEnum.NewMember}
            />
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6} lg={5}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <ContactsChart memberStats={memberStatsData} isLoading={isLoading} />
          {hasError && <ChartFailMessage />}
          {/* {isLoading && <LoadingChart />} */}
        </Paper>
      </Grid>
      <Grid item xs={12} lg={2}>
        <Grid container spacing={responsiveSpacing}>
          <Grid item xs={6} lg={12}>
            <StatBoxSmall isLoading={isLoading} label={t('dashboard:members.memberType.ordinary')} count={memberStatsData?.nrOfOrdinary || 0} />
          </Grid>
          <Grid item xs={6} lg={12}>
            <StatBoxSmall isLoading={isLoading} label={t('dashboard:members.memberType.family')} count={memberStatsData?.nrOfFamily || 0} />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
